import { SectionOneFormState } from '@/features/section-1/infra/ui/view-models/useSectionOne';
import {
  Amendment,
  AmendmentStatus,
  Section1History,
  Section2History,
} from '../../application/domain/amendment';
import { AmendmentResponseDto, SnapshotDocumentsDto } from '../dto/amendment-response.dto';
import type { LabeledUploadAsOf } from '@/features/section-2/application/domain/entities/Section2';
import { SectionOneSteps } from '@/features/section-1/application/domain/models/section-one-steps';
import { Section2Steps } from '@/features/section-2/application/domain/models/section-2-steps';
import { SupplementCoreFields } from '@/features/supplement/infra/repositories/dto/res/SupplementResponseDto';
import { CitizenshipSelectionOptions } from '@/features/section-1/features/citizenship-selection/application/domain/entities/CitizenshipSelection';
import { formatDate } from '@/shared/infra/utils/utils';

export class AmendmentMapper {
  static toEntity(amendmentDto: AmendmentResponseDto): Amendment {
    const section1Snapshot = amendmentDto.section1Snapshot;
    const section1FormFields = section1Snapshot?.coreFields;
    const supplementAHistory = section1Snapshot?.supplementAHistory;
    const section1History =
      section1Snapshot && section1FormFields
        ? new Section1History(
            {
              firstName: section1FormFields.firstName,
              lastName: section1FormFields.lastName,
              middleInitial: section1FormFields.middleName,
              otherName: section1FormFields.otherName,
              address: section1FormFields.address,
              aptNumber: section1FormFields.appartmentNumber,
              city: section1FormFields.city,
              // Both are nullable on a digitized paper I-9; an unreadable value leaves the
              // amendment field blank rather than being coerced into a wrong one.
              state: section1FormFields.stateId ?? undefined,
              zipCode: section1FormFields.zipCode,
              dob: section1FormFields.dateOfBirth ?? undefined,
              ssn: section1FormFields.ssn,
              email: section1FormFields.email,
              phone: section1FormFields.phoneNumber,
              citizenshipStatus:
                section1FormFields.cityzenshipStatus as CitizenshipSelectionOptions,
              uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork:
                section1FormFields.uscisANumberOrAuthorizedAlienNumberForAlienAuthorizedToWork,
              uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident:
                section1FormFields.uscisANumberOrAuthorizedAlienNumberForLawfulPermanentResident,
              authorizedI94Number: section1FormFields.formI94AdminssionNumber,
              foreignPassportNumber: section1FormFields.foreignPassportNumber,
              passportCountry: section1FormFields.countryOfIssuanceForPassport,
              // Box 4 as that version attested it: its date or its chosen N/A (VTK-2071), formatted
              // in UTC as the live Section 1 read does.
              expirationDate: section1FormFields.expirationDateForDocumentOfAlienAuthorizedToWork
                ? formatDate(
                    new Date(section1FormFields.expirationDateForDocumentOfAlienAuthorizedToWork)
                  )
                : null,
              workAuthorizationNoExpiration:
                section1FormFields.workAuthorizationNoExpiration === true,
              signature: section1FormFields.signatureOfEmployee,
              signatureDate: section1FormFields.dateOfSignature
                ? new Date(section1FormFields.dateOfSignature)
                : null,
              interpreterStatus: section1FormFields.isAssistedByPreparorOrTranslator ? 'Yes' : 'No',
              interpreters:
                supplementAHistory && supplementAHistory.length > 0
                  ? supplementAHistory.map((history) => {
                      return {
                        interpreterFirstName: history.firstName,
                        interpreterLastName: history.lastName,
                        interpreterMiddleInitial: history.middleName,
                        interpreterHomeAddress: history.address,
                        interpreterCity: history.city,
                        interpreterState: history.stateId,
                        interpreterZipcode: history.zipCode,
                        interpreterSignature: history.signature,
                        interpreterSignatureDate: new Date(history.signatureDate),
                        panelty_checkbox: history.isSection1AssistedAttested,
                      };
                    })
                  : [],
            },
            section1Snapshot.status as SectionOneFormState,
            section1Snapshot.i9FormId,
            SectionOneSteps.EmployerSelection
          )
        : null;

    const section2Snapshot = amendmentDto.section2Snapshot;
    const section2History = section2Snapshot
      ? new Section2History(
          {
            firstWorkDay: section2Snapshot.firstDayOfEmployment,
            delayReasonType: section2Snapshot.reasonOfDelay,
            delayReasonOther: section2Snapshot.otherReasonForDelay,
            listASelection: section2Snapshot.listADocKey,
            listBSelection: section2Snapshot.listBDocKey,
            listCSelection: section2Snapshot.listCDocKey,
            documentMetadata: {
              listA: section2Snapshot.listADocMetadata,
              listB: section2Snapshot.listBDocMetadata,
              listC: section2Snapshot.listCDocMetadata,
            },
            signatureText: section2Snapshot.signature,
            signatureTimestamp: new Date(section2Snapshot.signatureDate),
            signerFullName: section2Snapshot.signerName,
          },
          section2Snapshot.i9FormId,
          Section2Steps.EmploymentStart,
          null,
          section2Snapshot.businessName,
          section2Snapshot.businessAddress
        )
      : null;

    if (section2History) {
      section2History.alternativeProcedureUsed = section2Snapshot?.alternativeProcedureUsed ?? null;
    }

    if (section2History && amendmentDto.section2Documents) {
      section2History.labeledUploadsAsOf = labeledUploadsOf(amendmentDto.section2Documents);
    }

    const supplementBSnapshot = amendmentDto.supplementBSnapshot;
    const supplementBHistory = (supplementBSnapshot as unknown as SupplementCoreFields) ?? null;

    return new Amendment(
      amendmentDto.status as AmendmentStatus,
      amendmentDto.amendmentReason, //reason for amendment
      section1History,
      section2History,
      supplementBHistory,
      {
        state: amendmentDto.state,
        kind: amendmentDto.kind,
        triggeredAt: amendmentDto.triggeredAt ? new Date(amendmentDto.triggeredAt) : undefined,
        closedAt: amendmentDto.closedAt
          ? new Date(amendmentDto.closedAt)
          : amendmentDto.closedAt === null
            ? null
            : undefined,
      }
    );
  }
}
/**
 * The labeled uploads a snapshot had when it was written: its documents that are not a list's own,
 * with their front and back. The list's own document already shows from the snapshot's metadata.
 */
export function labeledUploadsOf(documents: SnapshotDocumentsDto): LabeledUploadAsOf[] {
  return documents.documents.flatMap((d) => {
    if (d.role === 'primary') return [];
    const url = (side: 'front' | 'back') => d.uploads.find((u) => u.side === side)?.url;
    return [
      {
        list: d.list,
        role: d.role,
        label: d.label || d.docKey,
        front: url('front'),
        back: url('back'),
      },
    ];
  });
}
